// supervisor-candidate-review-dispatch.ts
//
// P20: dispatches the `review-local-v1` session against the CANDIDATE — the
// commit the mission is about to push. Split out of
// supervisor-run-until-idle.ts (≤200-line budget, same convention as every
// other supervisor-*.ts split) so the "when does the review run" predicate
// reads in one place.
//
// Why this is not a seeded ticket (measured, not assumed): a ticket seeded at
// `autopilot init` can only depend on tickets that exist at init time, so
// `depends_on: ["T-0002"]` (plan) makes it ready IMMEDIATELY after planning —
// before a single ticket the plan produced has run. On a real e2e mission the
// report was promoted at SHA `1a8d9a18` while the release flow looked it up
// at `7cfac65a`. Making the plan worker declare the dependency instead would
// hand the ordering of its own review to the worker — the Bug K shape.
import {
  buildCleanReviewCandidateTicket,
  buildReviewCandidateTicket,
} from "./review-candidate-session.js";
import type { TicketRuntimeStatus } from "./scheduler.js";
import { prepareStaleReviewRerun } from "./supervisor-candidate-review-rerun.js";
import {
  type CandidateReviewSettlement,
  readCandidateReviewSettlement,
} from "./supervisor-candidate-review-settlement.js";
import { runOneSupervisorTick, type SupervisorTickInput } from "./supervisor-loop.js";

export type CandidateReviewDispatchResult =
  | "dispatched"
  | "already_settled"
  | "not_ready"
  | "gate_blocked"
  /** The one-re-offer-per-run fuse refused a stale review (supervisor-candidate-review-rerun.ts). */
  | "reoffer_refused";

/**
 * Runs the candidate-review session at most ONCE PER CANDIDATE — not once per
 * `run` (code review P3 H1: once per mission stranded a redirected candidate,
 * see supervisor-candidate-review-settlement.ts). The guard reads durable
 * state (MissionStore's ticket map + the reviewed candidate, the same source
 * `buildSchedulerProjection` reads), so a second `avatar autopilot run` on a
 * finished mission does not burn another Claude session. An in-memory flag was
 * tried first and measured wrong: 2 `attempt_started` events for the review
 * ticket across two consecutive runs (H1, the same non-idempotent-side-effect
 * shape as Bug O in P18). A session left non-terminal (failed attempt, or a
 * supervisor killed mid-attempt) IS retried — that is orphan recovery, not
 * duplication.
 *
 * Two independent layers stop the repeat, and the e2e regression assertion was
 * verified to fail only when BOTH are removed: this guard, and the projection
 * below seeding the ticket's status from the same durable map (so
 * `nextReadyTicket` returns undefined for an already-settled session).
 *
 * The session's outcome deliberately does NOT gate the mission reaching
 * "done": a review that fails or writes no report simply leaves
 * `review-staging/` empty, and `resumeReleaseFlowIfConfigured` answers
 * `waiting_review_report` — the honest state. Blocking "done" here would
 * instead strand the mission with no operator-visible reason.
 */
export async function dispatchCandidateReviewSessionIfNeeded(
  input: Omit<SupervisorTickInput, "projection">,
  settlement?: CandidateReviewSettlement,
): Promise<CandidateReviewDispatchResult> {
  // C2 (plan 20260915-1650 phase-03): two reviewers, dispatched one per tick
  // in a fixed order — local first, then the clean-context second reviewer.
  //
  // Sequential rather than both-at-once because the caller `continue`s on
  // "dispatched" and comes back here next tick; by then T-9001's status is
  // durable in the store, this guard reports it settled, and the clean
  // reviewer takes its turn. No change to the loop, and each session is still
  // dispatched through a projection containing only itself.
  //
  // Order matters only in that the clean reviewer must not be starved: if
  // T-9001 ends non-terminal (killed mid-attempt), the first branch retries
  // it — orphan recovery takes priority over starting a second session on a
  // candidate whose first review never finished.
  const { local, clean, staleDone, headSha } =
    settlement ?? (await readCandidateReviewSettlement(input));
  if (local && clean) return "already_settled";

  const reviewTicket = local ? buildCleanReviewCandidateTicket() : buildReviewCandidateTicket();
  // H1: a `done` for another candidate is offered again as `todo`, with a
  // fresh failure budget and at most once per ticket@HEAD per run (M-A/M-B).
  const stale = staleDone.has(reviewTicket.id);
  if (stale && !(await prepareStaleReviewRerun(input, reviewTicket.id, headSha ?? ""))) {
    return "reoffer_refused";
  }
  const status = stale
    ? "todo"
    : ((input.store.state.tickets[reviewTicket.id] as TicketRuntimeStatus) ?? "todo");
  const result = await runOneSupervisorTick({
    ...input,
    projection: { tickets: [reviewTicket], ticketStatus: { [reviewTicket.id]: status } },
  });

  // `gate_blocked` is NOT a dispatch. This ticket's kind is `review`, not a
  // bootstrap kind, so evaluateFeatureDispatchGateGuard requires G0/G1/G2 —
  // and on real mission ME-2026-dc845a G2 was still `pending`. Mapping
  // "anything not idle" to "dispatched" made the caller `continue` every tick
  // until maxTicks and report `idle` after 40 ticks, hiding the one fact the
  // operator needed: a gate is waiting for them.
  if (result.kind === "gate_blocked") return "gate_blocked";

  // `idle` means the projection offered nothing ready — with a single ticket
  // that only happens when the store already holds it non-"todo" (e.g. left
  // "in_progress" by a kill the reconciler has not reset yet). Reporting
  // "not_ready" lets the caller fall through to "done": looping would spin to
  // maxTicks with nothing able to change.
  return result.kind === "idle" ? "not_ready" : "dispatched";
}
